/**
 * Song Requests Settings Page
 *
 * A channel's song-request settings and bans (R25). Rebuilt in the design
 * system in R25 chat 2: semantic tokens, `useToast` for every action's
 * outcome, `apiErrorMessage` for every failure, an inline banner only when
 * the page cannot load.
 *
 * SETTINGS go through CORE's settings API (`/api/modules/song_requests/...`):
 * layering (module defaults <- admin global <- channel), locks, invalidation.
 * Only the keys this page knows are saved - a retired setting a channel still
 * has stored is left alone and ignored by the module (R25 S11).
 *
 * BANS go through the module's own routes (`./api.ts`), recorded with the
 * signed-in user as `banned_by` (R25 S6).
 */

import { useCallback, useEffect, useState } from 'react'
import { Ban as BanIcon, KeyRound, Loader2, Music, Plus, Save, TestTube, Trash2 } from 'lucide-react'
import clsx from 'clsx'
import api, { apiErrorMessage } from '@/api/client'
import { useToast } from '@/components/Toast'
import type { ModuleSettingsProps } from '../types'
import { mmss, songRequestsApi, type Ban, type BanType } from './api'

/** The settings this page edits - the module's schema (backend/config.py). */
interface SongRequestsConfig {
  endpoint_url: string
  auth_token: string
  max_song_length_seconds: number
  max_queue_length: number
  max_requests_per_user: number
  user_cooldown_seconds: number
  enable_priority_queue: boolean
  priority_for_subscribers: boolean
  priority_for_vips: boolean
  announce_now_playing: boolean
}

type ConfigKey = keyof SongRequestsConfig

const KNOWN_KEYS: ConfigKey[] = [
  'endpoint_url',
  'auth_token',
  'max_song_length_seconds',
  'max_queue_length',
  'max_requests_per_user',
  'user_cooldown_seconds',
  'enable_priority_queue',
  'priority_for_subscribers',
  'priority_for_vips',
  'announce_now_playing',
]

/** The numeric fields and their bounds (the schema's ge / le). */
const NUMBER_FIELDS: Record<
  'max_song_length_seconds' | 'max_queue_length' | 'max_requests_per_user' | 'user_cooldown_seconds',
  { label: string; help: string; min: number; max: number }
> = {
  max_song_length_seconds: {
    label: 'Longest song (seconds)',
    help: "The track's own length. Live streams are always refused.",
    min: 60,
    max: 3600,
  },
  max_queue_length: { label: 'Max queue length', help: 'Most requests waiting at once.', min: 1, max: 500 },
  max_requests_per_user: {
    label: 'Max requests per viewer',
    help: 'Counts requests queued or playing - not a lifetime total.',
    min: 1,
    max: 50,
  },
  user_cooldown_seconds: {
    label: 'Viewer cooldown (seconds)',
    help: "Time between one viewer's accepted requests. !sr also has a 10s anti-spam cooldown (Commands page).",
    min: 0,
    max: 3600,
  },
}

interface ModuleSettings {
  enabled: boolean
  settings: Record<string, unknown>
  global_settings: Record<string, unknown>
  user_settings: Record<string, unknown>
  overridable_keys: string[]
}

interface TestResult {
  success: boolean
  message: string
  details?: { current_track?: string }
}

interface AuthorizeResult {
  success: boolean
  message: string
}

const BAN_TYPES: { value: BanType; label: string; placeholder: string; help: string }[] = [
  {
    value: 'song',
    label: 'Song',
    placeholder: 'https://music.youtube.com/watch?v=… or a video id',
    help: 'A YouTube or YouTube Music link, or the 11-character video id.',
  },
  {
    value: 'artist',
    label: 'Artist',
    placeholder: 'Artist name',
    help: "Matches any of a track's artists, ignoring case.",
  },
  {
    value: 'requester',
    label: 'Viewer',
    placeholder: 'Twitch login',
    help: 'Someone who has requested a song or chatted on this channel.',
  },
]

function toConfig(settings: Record<string, unknown>): SongRequestsConfig {
  return Object.fromEntries(KNOWN_KEYS.map((k) => [k, settings[k]])) as unknown as SongRequestsConfig
}

/** A field row: label, optional "Admin only" lock, control, help text. */
function Field({
  label,
  help,
  locked,
  htmlFor,
  error,
  children,
}: {
  label: string
  help?: string
  locked: boolean
  htmlFor?: string
  error?: string | null
  children: React.ReactNode
}) {
  return (
    <div className={clsx(locked && 'opacity-60')}>
      <label className="label flex items-center gap-2" htmlFor={htmlFor}>
        {label}
        {locked && <span className="badge-neutral">Admin only</span>}
      </label>
      {children}
      {error ? <p className="error-text">{error}</p> : help ? <p className="help-text">{help}</p> : null}
    </div>
  )
}

function Check({
  checked,
  disabled,
  onChange,
  label,
  help,
}: {
  checked: boolean
  disabled: boolean
  onChange: (v: boolean) => void
  label: string
  help?: string
}) {
  return (
    <label className={clsx('flex items-start gap-3', disabled ? 'opacity-60' : 'cursor-pointer')}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 accent-synth-magenta"
      />
      <span>
        <span className="text-sm text-text-secondary flex items-center gap-2">
          {label}
          {disabled && <span className="badge-neutral">Admin only</span>}
        </span>
        {help && <span className="help-text block mt-0">{help}</span>}
      </span>
    </label>
  )
}

export function SongRequestsSettings({ channelId, moduleName }: ModuleSettingsProps) {
  const { success, error } = useToast()
  const [settings, setSettings] = useState<ModuleSettings | null>(null)
  const [config, setConfig] = useState<SongRequestsConfig | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [testedOk, setTestedOk] = useState(false)
  const [isAuthorizing, setIsAuthorizing] = useState(false)

  const [bans, setBans] = useState<Ban[] | null>(null)
  const [banType, setBanType] = useState<BanType>('artist')
  const [banValue, setBanValue] = useState('')
  const [banReason, setBanReason] = useState('')
  const [banBusy, setBanBusy] = useState<string | null>(null)

  const settingsPath = `/api/modules/${moduleName}/settings/${channelId}`

  const applySettings = useCallback((data: ModuleSettings) => {
    setSettings(data)
    setConfig(toConfig(data.settings))
  }, [])

  useEffect(() => {
    let alive = true
    setLoadError(null)
    Promise.all([api.get<ModuleSettings>(settingsPath), songRequestsApi.bans(channelId)])
      .then(([data, banList]) => {
        if (!alive) return
        applySettings(data)
        setBans(banList)
      })
      .catch((err) => {
        if (alive) setLoadError(apiErrorMessage(err, 'Could not load the song-request settings'))
      })
    return () => {
      alive = false
    }
  }, [settingsPath, channelId, applySettings])

  const isLocked = (key: ConfigKey) => !(settings?.overridable_keys.includes(key) ?? false)

  const update = <K extends ConfigKey>(key: K, value: SongRequestsConfig[K]) => {
    if (config) setConfig({ ...config, [key]: value })
  }

  const numberError = (key: keyof typeof NUMBER_FIELDS): string | null => {
    if (!config) return null
    const v = config[key]
    const { min, max } = NUMBER_FIELDS[key]
    return Number.isInteger(v) && v >= min && v <= max ? null : `Between ${min} and ${max}.`
  }
  const invalid = (Object.keys(NUMBER_FIELDS) as (keyof typeof NUMBER_FIELDS)[]).some(
    (k) => !isLocked(k) && numberError(k) !== null,
  )

  const handleSave = async () => {
    if (!config || !settings || invalid) return
    setIsSaving(true)
    try {
      const patch: Record<string, unknown> = {}
      for (const key of KNOWN_KEYS) {
        if (!isLocked(key)) patch[key] = config[key]
      }
      applySettings(await api.put<ModuleSettings>(settingsPath, { settings: patch }))
      success('Song-request settings saved')
    } catch (err) {
      error(apiErrorMessage(err, 'Could not save the settings'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleTest = async () => {
    if (!config) return
    setIsTesting(true)
    try {
      const r = await api.post<TestResult>(`/api/modules/${moduleName}/test-connection`, {
        endpoint_url: config.endpoint_url,
        auth_token: config.auth_token,
      })
      setTestedOk(r.success)
      const track = r.details?.current_track ? ` Playing: ${r.details.current_track}.` : ''
      if (r.success) success(`${r.message}${track}`)
      else error(r.message)
    } catch (err) {
      setTestedOk(false)
      error(apiErrorMessage(err, 'The connection test failed'))
    } finally {
      setIsTesting(false)
    }
  }

  // The YTMD pairing handshake. Blocks up to ~35s while the streamer clicks
  // Allow in YTM Desktop; on success the server saves the token into this
  // channel's settings, so the settings are read again to show it.
  const handleAuthorize = async () => {
    if (!config) return
    setIsAuthorizing(true)
    try {
      const r = await api.post<AuthorizeResult>(`/api/modules/${moduleName}/authorize`, {
        channel_id: channelId,
        endpoint_url: config.endpoint_url,
      })
      if (!r.success) {
        error(r.message)
        return
      }
      success(r.message)
      try {
        applySettings(await api.get<ModuleSettings>(settingsPath))
      } catch {
        // The token is saved regardless; a reload shows it.
      }
    } catch (err) {
      error(
        apiErrorMessage(
          err,
          "Authorisation failed. Check YTM Desktop is running with the Companion Server (and its authorisation) enabled.",
        ),
      )
    } finally {
      setIsAuthorizing(false)
    }
  }

  const banSpec = BAN_TYPES.find((b) => b.value === banType)!

  const handleAddBan = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!banValue.trim()) return
    setBanBusy('add')
    try {
      const ban = await songRequestsApi.addBan(channelId, {
        ban_type: banType,
        value: banValue.trim(),
        reason: banReason.trim() || undefined,
      })
      setBans((prev) => [ban, ...(prev ?? [])])
      setBanValue('')
      setBanReason('')
      success(`Banned ${ban.label ?? ban.value}`)
    } catch (err) {
      error(apiErrorMessage(err, 'Could not add the ban'))
    } finally {
      setBanBusy(null)
    }
  }

  const handleRemoveBan = async (ban: Ban) => {
    setBanBusy(`rm-${ban.id}`)
    try {
      await songRequestsApi.removeBan(channelId, ban.id)
      setBans((prev) => (prev ?? []).filter((b) => b.id !== ban.id))
      success(`Unbanned ${ban.label ?? ban.value}`)
    } catch (err) {
      error(apiErrorMessage(err, 'Could not remove the ban'))
    } finally {
      setBanBusy(null)
    }
  }

  if (loadError) {
    return (
      <div className="card text-center py-12">
        <p className="text-state-danger">{loadError}</p>
      </div>
    )
  }

  if (!config || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-synth-magenta" size={32} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-synth-mint/10">
            <Music className="text-synth-mint" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-text-primary">Song Requests</h2>
            <p className="text-sm text-text-muted">Requests from chat, played through YTM Desktop</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving || invalid}
          className="btn-primary flex items-center gap-2"
        >
          {isSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
          Save changes
        </button>
      </div>

      {/* YTM Desktop connection */}
      <div className="card space-y-4">
        <h3 className="text-lg font-semibold text-text-primary">YTM Desktop connection</h3>

        <Field
          label="Companion Server URL"
          help="Where YTM Desktop's Companion Server listens, as the bot's host sees it."
          locked={isLocked('endpoint_url')}
          htmlFor="sr-endpoint"
        >
          <input
            id="sr-endpoint"
            type="text"
            value={config.endpoint_url}
            onChange={(e) => {
              update('endpoint_url', e.target.value)
              setTestedOk(false)
            }}
            disabled={isLocked('endpoint_url')}
            placeholder="http://192.168.1.100:9863"
            className="input"
          />
        </Field>

        <Field
          label="Token"
          help="Set by Authorise below. Kept with this channel's settings."
          locked={isLocked('auth_token')}
          htmlFor="sr-token"
        >
          <input
            id="sr-token"
            type="password"
            value={config.auth_token}
            onChange={(e) => update('auth_token', e.target.value)}
            disabled={isLocked('auth_token')}
            placeholder="Not authorised yet"
            className="input"
          />
        </Field>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleTest}
            disabled={isTesting || isAuthorizing}
            className="btn-secondary flex items-center gap-2"
          >
            {isTesting ? <Loader2 className="animate-spin" size={16} /> : <TestTube size={16} />}
            Test connection
          </button>
          {testedOk && (
            <button
              onClick={handleAuthorize}
              disabled={isAuthorizing || isTesting}
              className="btn-primary flex items-center gap-2"
            >
              {isAuthorizing ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />}
              Authorise PratBot with YTMD
            </button>
          )}
        </div>
        {isAuthorizing && (
          <p className="text-sm text-state-warning flex items-center gap-2">
            <Loader2 className="animate-spin shrink-0" size={16} />
            Waiting for approval — click <strong>Allow</strong> in YTM Desktop within 30 seconds.
          </p>
        )}
      </div>

      {/* Requests */}
      <div className="card">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Requests</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(Object.keys(NUMBER_FIELDS) as (keyof typeof NUMBER_FIELDS)[]).map((key) => {
            const spec = NUMBER_FIELDS[key]
            const err = isLocked(key) ? null : numberError(key)
            const help =
              key === 'max_song_length_seconds' && !err
                ? `${spec.help} Now ${mmss(config[key])}.`
                : spec.help
            return (
              <Field key={key} label={spec.label} help={help} locked={isLocked(key)} htmlFor={`sr-${key}`} error={err}>
                <input
                  id={`sr-${key}`}
                  type="number"
                  value={Number.isFinite(config[key]) ? config[key] : ''}
                  onChange={(e) => update(key, e.target.value === '' ? NaN : Number(e.target.value))}
                  disabled={isLocked(key)}
                  min={spec.min}
                  max={spec.max}
                  aria-invalid={err ? true : undefined}
                  className="input"
                />
              </Field>
            )
          })}
        </div>
      </div>

      {/* Priority */}
      <div className="card space-y-3">
        <h3 className="text-lg font-semibold text-text-primary mb-1">Priority</h3>
        <Check
          checked={config.enable_priority_queue}
          disabled={isLocked('enable_priority_queue')}
          onChange={(v) => update('enable_priority_queue', v)}
          label="Priority requests play first"
          help="A priority request goes ahead of everyone else's, in the order they arrived."
        />
        {config.enable_priority_queue && (
          <div className="ml-7 space-y-2">
            <Check
              checked={config.priority_for_subscribers}
              disabled={isLocked('priority_for_subscribers')}
              onChange={(v) => update('priority_for_subscribers', v)}
              label="Subscribers"
            />
            <Check
              checked={config.priority_for_vips}
              disabled={isLocked('priority_for_vips')}
              onChange={(v) => update('priority_for_vips', v)}
              label="VIPs"
            />
          </div>
        )}
      </div>

      {/* Chat */}
      <div className="card space-y-3">
        <h3 className="text-lg font-semibold text-text-primary mb-1">Chat</h3>
        <Check
          checked={config.announce_now_playing}
          disabled={isLocked('announce_now_playing')}
          onChange={(v) => update('announce_now_playing', v)}
          label="Announce each request as it starts"
          help="“Now playing: … — requested by @…”, said by this channel's bot. The streamer's own music is never announced."
        />
        <p className="help-text">
          Who may use !sr and !skip is set per command on the Commands page.
        </p>
      </div>

      {/* Bans */}
      <div className="card space-y-4">
        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <BanIcon size={18} />
          Bans
        </h3>
        <p className="help-text mt-0">
          A banned song, artist or viewer is refused at !sr. Bans take effect at once; they don't
          remove anything already queued.
        </p>

        <form onSubmit={handleAddBan} className="grid grid-cols-1 md:grid-cols-[9rem_1fr_1fr_auto] gap-2 items-start">
          <select
            value={banType}
            onChange={(e) => setBanType(e.target.value as BanType)}
            className="select"
            aria-label="Ban type"
          >
            {BAN_TYPES.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>
          <div>
            <input
              value={banValue}
              onChange={(e) => setBanValue(e.target.value)}
              placeholder={banSpec.placeholder}
              className="input"
              aria-label="What to ban"
            />
            <p className="help-text">{banSpec.help}</p>
          </div>
          <input
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            placeholder="Reason (optional)"
            maxLength={500}
            className="input"
            aria-label="Reason"
          />
          <button
            type="submit"
            disabled={!banValue.trim() || banBusy !== null}
            className="btn-secondary flex items-center gap-2"
          >
            {banBusy === 'add' ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
            Ban
          </button>
        </form>

        {bans && bans.length === 0 && <p className="text-sm text-text-muted">No bans on this channel.</p>}
        {bans && bans.length > 0 && (
          <ul className="space-y-1">
            {bans.map((ban) => (
              <li key={ban.id} className="flex items-center gap-3 rounded bg-surface-base px-3 py-2">
                <span className="badge-danger w-16 justify-center shrink-0">
                  {BAN_TYPES.find((b) => b.value === ban.ban_type)?.label ?? ban.ban_type}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-text-primary truncate">
                    {ban.label ?? ban.value}
                    {ban.label && ban.ban_type === 'song' && (
                      <span className="code-inline ml-2 text-xs">{ban.value}</span>
                    )}
                  </p>
                  <p className="text-xs text-text-muted truncate">
                    by {ban.banned_by}
                    {ban.created_at && ` · ${new Date(ban.created_at).toLocaleDateString()}`}
                    {ban.reason && ` · ${ban.reason}`}
                  </p>
                </div>
                <button
                  onClick={() => handleRemoveBan(ban)}
                  disabled={banBusy !== null}
                  className="btn-ghost btn-sm px-1.5 hover:text-state-danger"
                  title="Remove this ban"
                >
                  {banBusy === `rm-${ban.id}` ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default SongRequestsSettings
